import { PDFDocument } from "pdf-lib";
import { getPdfDocument } from "@/lib/pdfjs";

/**
 * Algunos PDFs (habitual en ERPs antiguos) vienen cifrados con contraseña de
 * propietario vacía. pdf-lib puede abrirlos con `ignoreEncryption`, pero copia
 * los flujos todavía cifrados: el archivo resultante se ve EN BLANCO.
 * En ese caso reconstruimos las páginas rasterizándolas con pdf.js, que sí
 * descifra el documento.
 */
async function rebuildPagesAsImages(file: File, groups: number[][]): Promise<File[]> {
  const buf = await file.arrayBuffer();
  const loadingTask = await getPdfDocument({ data: buf });
  const pdf = await loadingTask.promise;

  const baseName = file.name.replace(/\.pdf$/i, "");
  const results: File[] = [];

  try {
    for (let g = 0; g < groups.length; g++) {
      const pages = groups[g];
      if (!pages.length) continue;
      const out = await PDFDocument.create();

      for (const pageNum of pages) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2.2 });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext("2d")!;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvas, canvasContext: ctx, viewport } as never).promise;
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        canvas.width = 0;
        canvas.height = 0;

        const jpg = await out.embedJpg(dataUrl);
        const base = page.getViewport({ scale: 1 });
        const newPage = out.addPage([base.width, base.height]);
        newPage.drawImage(jpg, { x: 0, y: 0, width: base.width, height: base.height });
      }

      const bytes = await out.save();
      const ab = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(ab).set(bytes);
      const blob = new Blob([ab], { type: "application/pdf" });
      results.push(
        new File([blob], `${baseName}_p${g + 1}.pdf`, {
          type: "application/pdf",
          lastModified: Date.now(),
        }),
      );
    }
  } finally {
    await loadingTask.destroy();
  }

  return results;
}

/**
 * Divide un PDF en varios archivos según los grupos de páginas indicados.
 * @param file PDF original.
 * @param groups Array de grupos; cada grupo es un array de índices de página 1-based.
 * @returns Array de File con el nombre base del original + sufijo _p{n}.
 */
export async function splitPdfByGroups(file: File, groups: number[][]): Promise<File[]> {
  const buf = await file.arrayBuffer();
  const src = await PDFDocument.load(buf, { ignoreEncryption: true });

  if (src.isEncrypted) {
    return rebuildPagesAsImages(file, groups);
  }

  const baseName = file.name.replace(/\.pdf$/i, "");
  const results: File[] = [];

  for (let g = 0; g < groups.length; g++) {
    const pages = groups[g];
    if (!pages.length) continue;
    const out = await PDFDocument.create();
    const copied = await out.copyPages(src, pages.map((p) => p - 1));
    copied.forEach((p) => out.addPage(p));
    const bytes = await out.save();
    // Copiar a un ArrayBuffer nuevo evita conflictos de tipado con SharedArrayBuffer.
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab], { type: "application/pdf" });
    const partName = `${baseName}_p${g + 1}.pdf`;
    results.push(new File([blob], partName, { type: "application/pdf", lastModified: Date.now() }));
  }

  return results;
}

export async function getPdfPageCount(file: File): Promise<number> {
  const buf = await file.arrayBuffer();
  const doc = await PDFDocument.load(buf, { ignoreEncryption: true });
  return doc.getPageCount();
}
